import { useNavigate } from "react-router";

import { ProgramComponent } from "@/components/pages_logged_in/program/components/ProgramComponent";
import { AllSetForNow } from "@/components/pages_logged_in/program/components/ProgramDashboardFeed";
import { getSortedComponents } from "@/components/pages_logged_in/program/components/utils";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import {
  ComponentTypeEnum,
  MeetingStatus,
  ProgramComponentType,
  ProgramType,
} from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

import { HomeBlock } from "./HomeBlock";

interface ParticipantProgramBlockProps {
  program: ProgramType;
  components?: ProgramComponentType[];
  onClick?: () => void;
}

export function ParticipantProgramBlock({
  program,
  components,
  onClick,
}: ParticipantProgramBlockProps) {
  const navigate = useNavigate();

  const programComponents = ensureNonNullArray(components);

  const filteredParticipantComponents: ProgramComponentType[] =
    programComponents
      .map((component) => ({
        ...component,
        selectedCoach: [
          ComponentTypeEnum.OneOneCoachingIntro,
          ComponentTypeEnum.OneOneCoachingSession,
        ].includes(component.type)
          ? programComponents.find(
              (c) => c.id === component.parentComponent?.id,
            )?.selectedCoach
          : component.selectedCoach,
      }))
      .filter(
        (component) =>
          !(
            component.type === ComponentTypeEnum.OneOneCoachingIntro &&
            component.selectedCoach
          ) &&
          !(
            component.type === ComponentTypeEnum.OneOneCoachingSession &&
            !component.meetings?.find((meeting) =>
              [MeetingStatus.Ongoing, MeetingStatus.Soon].includes(
                meeting.status as MeetingStatus,
              ),
            )
          ),
      );

  const sortedParticipantComponents = getSortedComponents({
    components: filteredParticipantComponents.map((component) => ({
      ...component,
      requiredSendLaunchEmail: false,
      forAllParticipants: false,
    })),
    sortByDependency: true,
  });

  const participantOngoing = sortedParticipantComponents.sort((a, b) => {
    if (
      a.type === ComponentTypeEnum.OneOneCoachingIntro &&
      b.type !== ComponentTypeEnum.OneOneCoachingIntro
    )
      return -1;
    if (
      b.type === ComponentTypeEnum.OneOneCoachingIntro &&
      a.type !== ComponentTypeEnum.OneOneCoachingIntro
    )
      return 1;

    if (
      a.type === ComponentTypeEnum.OneOneCoachingIntro &&
      b.type === ComponentTypeEnum.OneOneCoachingIntro
    ) {
      if (a.targetStartDate && b.targetStartDate) {
        return (
          new Date(a.targetStartDate).getTime() -
          new Date(b.targetStartDate).getTime()
        );
      }
      if (a.targetStartDate) return -1;
      if (b.targetStartDate) return 1;
    }

    return 0;
  });

  const renderContent = () => {
    return (
      <div className="xs:p-5 xs:gap-3.5 flex flex-col md:gap-4 md:p-6">
        <div className="xs:text-lg font-semibold md:text-xl">Next</div>

        {participantOngoing?.map((component, index) => (
          <ProgramComponent
            key={component.id}
            component={component}
            program={program}
            role="participant"
            onEdit={() => {}}
            onDelete={() => {}}
            onClose={() => {}}
            collapsed={index > 0}
            mb={0}
            onClick={
              index > 0
                ? () =>
                    navigate(
                      `/programs/${program.id}#component_id_${component.id}`,
                    )
                : undefined
            }
            view="feed"
          />
        ))}
        {!participantOngoing?.length && <AllSetForNow mb={0} />}
      </div>
    );
  };

  return (
    <HomeBlock
      title={program.name}
      description={
        <div className="">
          <DateTimeDisplay
            time={program.targetLaunchDate}
            formatString="MMM d, yyyy"
          />
          {program.targetEndDate && (
            <>
              {" - "}
              <DateTimeDisplay
                time={program.targetEndDate}
                formatString="MMM d, yyyy"
              />
            </>
          )}
        </div>
      }
      content={renderContent()}
      onClick={onClick}
    />
  );
}
